import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Formik, FormikHelpers } from 'formik';
import clsx from 'classnames';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Button, Grid, Typography } from '@material-ui/core';

import { RegistrationForm as RegistrationFormType } from 'types';
import { Jumbotron } from 'components/jumbotron/jumbotron';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';
import { useWhoIs } from 'components/auth/auth-hook';
import { AuthCtx } from 'components/auth/auth-ctx';

import { postRegistration } from './registration-api';
import { RegistrationFormBasic } from './registration-form-basic';
import { validationSchema } from './registration-validation';

const useStyles = makeStyles(() =>
  createStyles({
    section: {
      marginTop: 50,
    },
    content: {
      padding: '0 0 50px 0',
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
    },
    loginButtonWrapper: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },
  })
);

export function RegistrationForm() {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { userInfo, userLoaded } = useContext(AuthCtx);
  const { showNotification } = useContext(NotificationCtx);
  const { whoIsInfo, whoIsLoaded } = useWhoIs();
  const history = useHistory();

  const whoIsInfoExist = !!whoIsInfo;

  const handleSubmit = async (
    values: RegistrationFormType,
    actions: FormikHelpers<RegistrationFormType>
  ) => {
    try {
      const response = await postRegistration(values);

      if (response) {
        showNotification('Registrace odeslána.', NotificationVariant.SUCCESS);
        actions.resetForm();
        history.push('');
      }
    } catch (e) {
      if (e?.code === 'EMAIL_ALREADY_EXISTS') {
        showNotification(
          'Registrace s tímto emailem již existuje.',
          NotificationVariant.ERROR
        );
      } else {
        showNotification(
          'Registrační formulář se nezdařilo odeslat.',
          NotificationVariant.ERROR
        );
      }
    }

    actions.setSubmitting(false);
  };

  useEffect(() => {
    if (userLoaded && userInfo) {
      history.push('/portfolio/badatelske-listy');
    }
  }, [history, userInfo, userLoaded]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Jumbotron title={formatMessage({ id: 'registration.jumbotron' })} />
        <Grid container className={clsx(classes.content, classes.center)}>
          <Grid item xs={10} md={5}>
            {whoIsInfoExist ? (
              <Grid container spacing={2} className={classes.center}>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    align="left"
                    className={classes.section}
                  >
                    {formatMessage({
                      id: 'registration.whoIsInfoExist',
                      defaultMessage:
                        'Data z CAS byly úspěšně načteny, doplňte chýbějící.',
                    })}
                    <br />
                    <br />
                    {formatMessage({
                      id: 'registration.whoIsInfoLaw',
                      defaultMessage:
                        'Tato žádost je žádostí o nahlížení do archiválií a vytvoření badatelského listu dle § 35 zákona č. 499/2004 Sb. o archivnictví a spisové službě v platném znění. Osobní údaje obsažené v badatelském listu jsou shromažďovány a zpracovávány podle zákona č. 499/2004 Sb. za účelem ochrany archiválií a slouží pro vnitřní potřebu archivu a nejsou poskytovány třetím stranám',
                    })}
                    <br />
                    <br />
                    {formatMessage({
                      id: 'registration.whoIsMandatoryFields',
                      defaultMessage: '*vždy povinné údaje',
                    })}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <>
                <div
                  className={clsx(classes.loginButtonWrapper, classes.section)}
                >
                  <Button component="span" variant="contained" color="primary">
                    <Typography component="span" variant="body2">
                      {formatMessage({
                        id: 'registration.beforeRegButton',
                        defaultMessage: 'Před registrací se přihlašte do CAS',
                      })}
                    </Typography>
                  </Button>
                </div>
                <Grid container spacing={2} className={classes.center}>
                  <Grid item xs={12}>
                    <Typography
                      variant="body1"
                      align="left"
                      className={classes.section}
                    >
                      {formatMessage(
                        {
                          id: 'registration.whoIsInfoLogin',
                          defaultMessage:
                            'Pokud ještě nemáte vytvořený účet v Centrální autentizační službě UK (CAS), budou vyplněná data použita pro založení účtu, ke kterému dojde po schválení žádosti správcem badatelny Archivu UK.<br></br> Pokud jste (nebo v cca od roku 2000 byl) studentem či zaměstnancem UK, pokračujte volbou <b>PŘIHLÁSIT SE DO CAS</b>, na přihlašovací stránce si případně můžete obnovit své zapomenuté heslo.',
                        },
                        { br: () => <br />, b: (msg) => <b>{msg}</b> }
                      )}
                      <br />
                      <br />
                      {formatMessage({
                        id: 'registration.whoIsInfoLaw',
                        defaultMessage:
                          'Tato žádost je žádostí o nahlížení do archiválií a vytvoření badatelského listu dle § 35 zákona č. 499/2004 Sb. o archivnictví a spisové službě v platném znění. Osobní údaje obsažené v badatelském listu jsou shromažďovány a zpracovávány podle zákona č. 499/2004 Sb. za účelem ochrany archiválií a slouží pro vnitřní potřebu archivu a nejsou poskytovány třetím stranám',
                      })}
                      <br />
                      <br />
                      {formatMessage({
                        id: 'registration.whoIsMandatoryFields',
                        defaultMessage: '*vždy povinné údaje',
                      })}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
            {whoIsLoaded && (
              <Formik<RegistrationFormType>
                initialValues={whoIsInfo ?? {}}
                onSubmit={handleSubmit}
                validateOnChange={false}
                validateOnBlur={false}
                validateOnMount={false}
                validationSchema={validationSchema}
              >
                {RegistrationFormBasic}
              </Formik>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
